import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Button from '@mui/material/Button';
import VerificationModal from '../verification-request/VerificationModal';
import { Box, IconButton, Modal, Pagination } from '@mui/material';
import { formatDate } from '../../utils/dateTime';
import Loader from '../common/Loader/Loader';
import SuspendCard from '../user-management/SuspendCard';
import CloseIcon from '@mui/icons-material/Close';
import ProfileCard from '../user-management/ProfileCard';
import { style2 } from '../user-management/UserList';
import ReportListUser from './ReportListUser';
import UserTableSkeleton from '../user-management/UserTableSkeleton';
import Tooltip from '@mui/material/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { initColumns, toggleColumn } from '../../containers/app-container/reducer/visibleColumnsSlice';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 600,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: 4,
  maxHeight: '80vh', // Adjust the height for scrolling
  overflow: 'auto', // Make content scrollable
};

const visibleColumnKey = [
  "email",
  "userName",
  "totalCount",
  "action"
]
function ReportTable({
  data,
  totalPages,
  currentPage,
  handlePageChange,
  itemsPerPage,
  loading,
  searchTerm,
  setSearchTerm,
  handleSearch,
  fetchData,
  groupby,
  setGroupBy,
  initialLoad
}) {
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const tableContainerRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false);
  const [selectedVerificationType, setSelectedVerificationType] = useState(null);
  const [showRejectReasonsModal, setShowRejectReasonsModal] = useState(false);
  const [showVerificationViewer, setShowVerificationViewer] = useState(true);
  const [selectedUser, setSelectedUser] = useState(false);
  const [openSuspend, setOpenSuspend] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Reported User');
  const visibleColumns = useSelector(state => state.visibleColumns.pages.reports || {});

  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  // Scroll thumb width calculation
  const thumbWidth = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.clientWidth / scrollState.scrollWidth;
      return Math.max(ratio * scrollState.clientWidth, 50);
    }
    return 100;
  }, [scrollState]);

  // Scroll thumb position
  const thumbPosition = useMemo(() => {
    if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
      const ratio = scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth);
      return ratio * (scrollState.clientWidth - thumbWidth);
    }
    return 0;
  }, [scrollState, thumbWidth]);

  const updateScrollState = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setScrollState({
        scrollLeft,
        scrollWidth,
        clientWidth,
      });
    }
  };

  const handleTableScroll = () => {
    updateScrollState();
  };

  const handleStickyScroll = (e) => {
    if (!tableContainerRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const thumbCenter = thumbWidth / 2;

    const newScrollLeft = (clickX - thumbCenter) / (trackRect.width - thumbWidth) *
      (scrollState.scrollWidth - scrollState.clientWidth);

    tableContainerRef.current.scrollLeft = newScrollLeft;
  };

  const handleThumbDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startScrollLeft = scrollState.scrollLeft;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const trackWidth = scrollState.clientWidth - thumbWidth;
      const scrollableWidth = scrollState.scrollWidth - scrollState.clientWidth;

      if (trackWidth > 0) {
        const newScrollLeft = startScrollLeft + (deltaX / trackWidth) * scrollableWidth;
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, scrollableWidth));
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleOpen = (verificationType, user) => {
    setSelectedVerificationType(verificationType);
    setOpen(true);
    setShowVerificationViewer(true);
    setSelectedUser(user);
  };

  useEffect(() => {
    if (initialLoad) {
      fetchData()
    }
  }, [groupby])

    useEffect(() => {
    dispatch(
      initColumns({
        pageKey: "reports",
        columns: visibleColumnKey
      })
    );
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.removeEventListener('resize', updateScrollState);
    };
  }, [data, visibleColumns]);

  const handleOpenSuspend = (user) => {
    setSelectedUser(user);
    setOpenSuspend(true);
  };

  const handleOpenProfile = (user) => {
    setSelectedUser(user)
    setOpenReportModal(true)
  };

  const handleCloseModal = () => {
    setOpenReportModal(false)
  };

  const handleCloseSuspend = () => setOpenSuspend(false);
  const handleCloseProfile = () => setOpenReportModal(false);

  const handleReject = () => {
    setShowRejectReasonsModal(true);
    setShowVerificationViewer(false);
  };

  const handleRequestReUpload = () => {
    setShowRejectReasonsModal(true);
    setShowVerificationViewer(false);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);

    let filterValue;

    switch (filter) {
      case 'Reported User':
        filterValue = 'reportedUser';
        dispatch(
          toggleColumn({
            pageKey: "reports",
            column: "action",
            value:true
          }) );
        break;
      case 'Reported By':
        filterValue = 'reportedBy';
        dispatch(
          toggleColumn({
            pageKey: "reports",
            column: "action",
            value:false
          }) );
        break;
      default:
        filterValue = 'reportedUser';
        break;
    }

    setGroupBy(filterValue)
  };

  const handleColumnToggle = (column) => {
    dispatch(
    toggleColumn({
      pageKey: "reports",
      column: column
    })
  );
  };

  // const filteredData = data

  const filteredData = useMemo(() => {
    if (!searchTerm?.trim()) return data;
    const term = searchTerm.toLowerCase();

    return data.filter((item) =>
      item?.userDetails?.email?.toLowerCase().includes(term) ||
      (item?.userDetails?.firstName ?? item?.userDetails?.userName)?.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);


  const columnHeaders = [
    { key: 'email', label: 'Email' },
    { key: 'userName', label: 'Username' },
    { key: 'totalCount', label: 'Count' },
    { key: 'action', label: 'View' },
  ];

  const handleOpenModal = (verificationType) => {
    setSelectedVerificationType(verificationType);
    setOpen(true);
  };

  useEffect(() => {
    // if(searchTerm===undefined || searchTerm===null) return
    if (searchTerm === undefined || searchTerm === null) return
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      fetchData(controller.signal)
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.error("Search error:", err);
          }
        });
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };

  }, [searchTerm]);

  return (
    <>
      <div className="mb-0 d-flex justify-content-between align-items-center">
        <h6 className="text-lg mb-0">Report Request</h6>
        <div className="d-flex align-items-center gap-2">
          <form className="navbar-search" onSubmit={e => e.preventDefault()}>
            <input
              type="text"
              name="search"
              placeholder="Search"
              value={searchTerm}
              autoComplete="off"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="icon">
              <iconify-icon icon="ion:search-outline" />
            </span>
          </form>
          {/* <Tooltip title="Download Data" placement="top" arrow>
            <button className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light">
              <SimCardDownloadOutlinedIcon />
            </button>
          </Tooltip> */}

          <div className="dropdown">
            <Tooltip title="Manage Columns" arrow placement="top">
              <button
                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <ViewColumnOutlinedIcon />
              </button>
            </Tooltip>
            <ul className="dropdown-menu manage-list">
              {columnHeaders.map((column) => (
                <li
                  key={column.key}
                  className="dropdown-item d-flex align-items-center gap-2"
                >
                  <input
                    className="form-check-input"
                    style={{ width: '18px', height: '18px' }}
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() => handleColumnToggle(column.key)}
                  />
                  <label className="ms-2">{column.label}</label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="d-flex align-items-center gap-3">
        <button
          className={`badge text-sm fw-medium px-20 py-12 radius-6 ${activeFilter === 'Reported User'
            ? 'text-warning-900 bg-warning-50'
            : 'bg-light-100 text-secondary-light'
            }`}
          onClick={() => handleFilterChange('Reported User')}
        >
          Reported User
        </button>
        <button
          className={`badge text-sm fw-medium px-20 py-12 radius-6 ${activeFilter === 'Reported By'
            ? 'text-warning-900 bg-warning-50'
            : 'bg-light-100 text-secondary-light'
            }`}
          onClick={() => handleFilterChange('Reported By')}
        >
          Reported By
        </button>
      </div>

      {/* Table */}
      <div className="table-scroll-container mt-3">
        <div
          ref={tableContainerRef}
          className="custom-horizontal-scroll scroll-sm"
          onScroll={handleTableScroll}
         style={{ 
    maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
    overflow: 'auto',
    position: 'relative'
  }}
        >
          <table
            className="table bordered-table sm-table mb-0"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              position: 'relative',
            }}
          >
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                <th>#</th>
                {visibleColumns.email && <th>Email</th>}
                {visibleColumns.userName && <th className='text-center'>Username</th>}
                {visibleColumns.totalCount && <th className='text-center'>Count</th>}
                {visibleColumns.action && <th className='text-center'>Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <UserTableSkeleton
                  rows={10}
                  columns={Object.keys(visibleColumns).filter((key) => visibleColumns[key]).length + 1}
                />
              ) : filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    {visibleColumns.email && <td>{item?.userDetails?.email}</td>}
                    {visibleColumns.userName && (
                      <td className='text-center'>{item?.userDetails?.firstName ?? item?.userDetails?.userName}</td>
                    )}
                    {visibleColumns.totalCount && (
                      <td className='text-center'>{item.totalCount}</td>
                    )}
                    {visibleColumns.action && (
                      <td className="text-center">
                        <a
                          className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                          href="javascript:void(0)"
                          onClick={() => handleOpenProfile(item?.userDetails)}
                        >
                          View
                        </a>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="20" className="text-center py-3">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sticky Scroll Bar */}
        {scrollState.scrollWidth > scrollState.clientWidth && (
          <div className="sticky-scroll-container">
            <div
              className="sticky-scroll-track"
              onClick={handleStickyScroll}
            >
              <div
                ref={scrollThumbRef}
                className="sticky-scroll-thumb"
                style={{
                  width: `${thumbWidth}px`,
                  transform: `translateX(${thumbPosition}px)`
                }}
                onMouseDown={handleThumbDrag}
              />
            </div>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-center mt-16">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          sx={{
    "& .MuiPaginationItem-root": {
      color: "#000",  
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important",  
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }} 
        />
      </div>

      <VerificationModal
        open={open}
        handleCloseModal={handleCloseModal}
        selectedVerificationType={selectedVerificationType}
        showVerificationViewer={showVerificationViewer}
        setShowVerificationViewer={setShowVerificationViewer}
        showRejectReasonsModal={showRejectReasonsModal}
        handleRequestReUpload={handleRequestReUpload}
        handleReject={handleReject}
        user={selectedUser}
        fetchData={fetchData}
      />

      <Modal
        open={openSuspend}
        onClose={handleCloseSuspend}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
            }}
            onClick={handleCloseSuspend}
          >
            <CloseIcon />
          </IconButton>
          <SuspendCard
            userId={selectedUser?._id}
            handleClose={handleCloseSuspend}
             fetchData={fetchData}
          />
        </Box>
      </Modal>

      <Modal
        open={openReportModal}
        onClose={handleCloseProfile}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style2}>
          <Box
            sx={{
              position: 'sticky',
              top: '0px',
              backgroundColor: 'white',
              zIndex: 10,
              padding: '10px',
              borderBottom: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <IconButton
              sx={{
                position: 'absolute',
                top: 10,
                right: 0,
              }}
              onClick={handleCloseProfile}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <ReportListUser userId={selectedUser?._id} isHideReportRequest={false} ClassName="mb-3" />
        </Box>
      </Modal>
    </>
  );
}

export default ReportTable;